/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { breakpoints, useMediaQuery } from '@coursera/cds-core';

import { DEFAULT_PAGE_HEADER_HEIGHT } from 'bundles/composable-page-header/constants';
import { getMetaHeight, isMetaNavEnabled } from 'bundles/page-header/components/MetaNavigation';
import useScrollInfo from 'bundles/page-header/components/hooks/useScrollInfo';
import { useUserAgent } from 'bundles/page/contexts/UserAgentApiContext';

const META_NAV_DELTA = 50; // Numbers for scroll position and delta copied from DesktopHeaderControls.tsx
const HEADER_BREAKPOINT_MAX = '@media screen and (max-width: 1060px)';

const styles = {
  stickyFilterWrapper: css`
    position: relative;
    width: 100%;
    background-color: var(--cds-color-neutral-background-primary);

    /* meta nav collapsing transition */
    transition: top 0.2s ease-in-out;

    /* make sure always above other sections when fixed */
    z-index: 1;
  `,
  contentContainer: css`
    position: relative;
    height: 100%;
    width: 100%;

    /* match Grid item xs=12 */
    padding: var(--cds-spacing-100) var(--cds-spacing-200) var(--cds-spacing-100) var(--cds-spacing-50);

    /* Grid item responsive */
    @media screen and (max-width: 1023px) {
      padding: var(--cds-spacing-100);
    }

    @media screen and (max-width: 600px) {
      /* align sides to other sections */

      padding: var(--cds-spacing-50) var(--cds-spacing-200);
      margin: 0 calc(var(--cds-spacing-150) * -1);
      width: 100vw;
    }

    /* match PageGridContainer */
    max-width: 1440px;
    margin: 0 auto;

    /* PageGridContainer responsiveness */
    &.isFixed {
      padding: var(--cds-spacing-200) var(--cds-spacing-400);

      @media screen and (max-width: 1023px) {
        padding: var(--cds-spacing-100) var(--cds-spacing-200);
      }

      @media screen and (max-width: 600px) {
        padding: var(--cds-spacing-50) var(--cds-spacing-200);
        margin: 0;
      }
    }

    /* unified filter layout */
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);

    ${breakpoints.up('sm')} {
      flex-wrap: wrap;
    }

    ${breakpoints.down('sm')} {
      flex-wrap: nowrap;
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }
  `,
};

function useFilterBarBehavior(
  stickyBarRef: React.RefObject<HTMLDivElement>,
  shouldAffixFilterOnMobile: boolean
): [boolean, boolean, number] {
  const userAgent = useUserAgent();
  const isMobile = useMediaQuery(HEADER_BREAKPOINT_MAX, {
    defaultMatches: userAgent?.isMobileBrowser || userAgent?.isMobile,
  });
  const [metaNavHeight, setMetaNavHeight] = useState(0);
  const metaNavEnabled = isMetaNavEnabled();
  const [shouldMoveFilter, setShouldMoveFilter] = useState(false);
  const [shouldAffixFilter, setShouldAffixFilter] = useState(shouldAffixFilterOnMobile && isMobile);
  const [scrollPastHeader, setScrollPastHeader] = useState(false);
  // Hook for matching the header's behavior
  const { isScrollingDown, lastScrollPosition } = useScrollInfo({
    delta: metaNavEnabled ? META_NAV_DELTA : Number.MAX_VALUE,
  });
  const isMetaNavHidden = useMemo(
    () => !isMobile && lastScrollPosition >= 0 && isScrollingDown,
    [isMobile, lastScrollPosition, isScrollingDown]
  );
  const pageHeaderHeight = DEFAULT_PAGE_HEADER_HEIGHT + (isMetaNavHidden ? 0 : metaNavHeight);

  useEffect(() => {
    const currentMetaNavHeight = isMobile ? 0 : parseInt(getMetaHeight(), 10) || 0;
    if (currentMetaNavHeight !== metaNavHeight) setMetaNavHeight(currentMetaNavHeight);
  }, [isMobile, metaNavHeight]);

  useEffect(() => {
    const onScroll = () => {
      const filterBarTop = stickyBarRef.current?.getBoundingClientRect().top || 0;
      const isScrollPastHeader = filterBarTop - pageHeaderHeight <= 0;
      if (isScrollPastHeader !== scrollPastHeader) {
        setScrollPastHeader(isScrollPastHeader);
      }
    };

    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, [pageHeaderHeight, stickyBarRef, scrollPastHeader]);

  useEffect(() => {
    const shouldFix = scrollPastHeader || (shouldAffixFilterOnMobile && isMobile);
    const shouldMove = isMetaNavHidden && shouldFix;

    if (shouldMove !== shouldMoveFilter) {
      setShouldMoveFilter(shouldMove);
    }
    if (shouldFix !== shouldAffixFilter) {
      setShouldAffixFilter(shouldFix);
    }
  }, [isMetaNavHidden, isMobile, shouldAffixFilter, shouldAffixFilterOnMobile, shouldMoveFilter, scrollPastHeader]);

  return [shouldMoveFilter, shouldAffixFilter, metaNavHeight];
}

type Props = {
  children?: React.ReactNode;
  shouldAffixFilterOnMobile?: boolean;
  filterComponents?: React.ReactNode[];
};

// Sticky behavior copied from `static/bundles/page-header/components/SmartScrollWrapper.tsx`
const StickyFilterBar = ({ children, shouldAffixFilterOnMobile = true, filterComponents }: Props) => {
  const stickyBarRef = useRef<HTMLDivElement>(null);
  const [shouldMoveFilter, shouldAffixFilter, metaNavHeight] = useFilterBarBehavior(
    stickyBarRef,
    shouldAffixFilterOnMobile
  );

  // these dynamic styles are using inline styles for performance reasons
  const affixStyle = useMemo(
    () => ({
      margin: 0,
      position: 'fixed' as const,
      top: DEFAULT_PAGE_HEADER_HEIGHT + (shouldMoveFilter ? 0 : metaNavHeight),
      left: 0,
      borderTop: shouldMoveFilter ? '1px solid var(--cds-color-neutral-stroke-primary-weak)' : '',
    }),
    [shouldMoveFilter, metaNavHeight]
  );

  return (
    <div className="rc-StickyFilterBar" data-testid="sticky-filter-bar" ref={stickyBarRef}>
      <div
        css={styles.stickyFilterWrapper}
        style={shouldAffixFilter ? affixStyle : {}}
        data-testid="sticky-filter-wrapper"
      >
        <div css={styles.contentContainer} className={`sticky-filter-bar ${shouldAffixFilter ? 'isFixed' : ''}`}>
          {filterComponents || children}
        </div>
      </div>
    </div>
  );
};

export default StickyFilterBar;
